import {useCallback, useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {readEndpoint} from 'redux-json-api'

import useEthicalWills from './useEthicalWills'
import {cleanEthicalWills} from '../lifeStoryTools'

/**
 * Ethical wills for the everplan being viewed. On the viewer's own everplan
 * this is useEthicalWills (shared status in the redux store); on another
 * person's everplan (deputy preview) it reads the everplan-filtered endpoint,
 * which only returns published stories the viewer's permissions allow, with
 * fetch status local to the mount.
 */
const useEverplanEthicalWills = ({everplanId = null, ownEverplan = true} = {}) => {
  const viewingOtherEverplan = Boolean(everplanId) && !ownEverplan

  const ownCollection = useEthicalWills({skip: viewingOtherEverplan})

  const dispatch = useDispatch()
  const rawEthicalWills = useSelector(state => state.api['ethical-wills'])
  const [status, setStatus] = useState('loading')

  const refetchOtherEverplanEthicalWills = useCallback(
    () => dispatch(readEndpoint(`ethical-wills?filter[everplan_id]=${everplanId}`))
      .then(response => {
        setStatus('loaded')

        return response
      })
      .catch(() => setStatus('failed')),
    [dispatch, everplanId]
  )

  useEffect(() => {
    if (!viewingOtherEverplan) return

    setStatus('loading')
    refetchOtherEverplanEthicalWills()
  }, [viewingOtherEverplan, refetchOtherEverplanEthicalWills])

  if (!viewingOtherEverplan) return ownCollection

  return {
    loadingEthicalWills: status === 'loading',
    ethicalWillsUnavailable: status === 'failed',
    // The redux-json-api store accumulates every fetched ethical will (the
    // viewer's own included), so scope back down to the viewed everplan.
    ethicalWills: cleanEthicalWills(rawEthicalWills)
      .filter(ethicalWill => `${ethicalWill.get('everplan-id')}` === `${everplanId}`),
    refetchEthicalWills: refetchOtherEverplanEthicalWills
  }
}

export default useEverplanEthicalWills
